import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';

/**
 * Tooltip component that uses React Portal to render tooltips without affecting layout
 * @param {React.ReactNode} children - The element that triggers the tooltip
 * @param {string} content - The text content of the tooltip
 * @param {string} position - Position of the tooltip: 'top', 'bottom', 'left', or 'right'
 * @returns {JSX.Element} - Tooltip component
 */
const Tooltip = ({ children, content, position = 'top' }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const childRef = useRef(null);
  
  // Add keyframe animation
  useEffect(() => {
    if (!document.getElementById('tooltip-animation-style')) {
      const style = document.createElement('style');
      style.id = 'tooltip-animation-style';
      style.innerHTML = `
        @keyframes tooltipFadeIn {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
      `;
      document.head.appendChild(style);
      
      return () => {
        const styleElement = document.getElementById('tooltip-animation-style');
        if (styleElement) document.head.removeChild(styleElement);
      };
    }
  }, []);

  // Calculate tooltip position when visibility changes
  useEffect(() => {
    if (isVisible && childRef.current) {
      const updatePosition = () => {
        const rect = childRef.current.getBoundingClientRect();
        const scrollLeft = window.scrollX || document.documentElement.scrollLeft;
        const scrollTop = window.scrollY || document.documentElement.scrollTop;
        
        // Get tooltip element to calculate its dimensions
        const tooltipEl = document.getElementById('portal-tooltip');
        if (!tooltipEl) return;
        
        const tooltipRect = tooltipEl.getBoundingClientRect();
        const tooltipWidth = tooltipRect.width;
        const tooltipHeight = tooltipRect.height;
        
        // Calculate position based on specified direction
        let top, left;
        
        switch (position) {
          case 'top':
            left = rect.left + scrollLeft + (rect.width / 2) - (tooltipWidth / 2);
            top = rect.top + scrollTop - tooltipHeight - 10;
            break;
          case 'bottom':
            left = rect.left + scrollLeft + (rect.width / 2) - (tooltipWidth / 2);
            top = rect.bottom + scrollTop + 10;
            break;
          case 'left':
            left = rect.left + scrollLeft - tooltipWidth - 10;
            top = rect.top + scrollTop + (rect.height / 2) - (tooltipHeight / 2);
            break;
          case 'right':
            left = rect.right + scrollLeft + 10;
            top = rect.top + scrollTop + (rect.height / 2) - (tooltipHeight / 2);
            break;
          default:
            left = rect.left + scrollLeft + (rect.width / 2) - (tooltipWidth / 2);
            top = rect.top + scrollTop - tooltipHeight - 10;
        }
        
        // Boundary checking to make sure tooltip stays within viewport
        const rightBoundary = window.innerWidth - tooltipWidth - 10;
        const bottomBoundary = window.innerHeight - tooltipHeight - 10;
        
        if (left < 10) left = 10;
        if (left > rightBoundary) left = rightBoundary;
        if (top < 10) top = 10;
        if (top > bottomBoundary) top = bottomBoundary;
        
        setTooltipPosition({ top, left });
      };
      
      // Initial position calculation
      updatePosition();
      
      // Add event listeners for recalculation on scroll/resize
      window.addEventListener('scroll', updatePosition);
      window.addEventListener('resize', updatePosition);
      
      return () => {
        window.removeEventListener('scroll', updatePosition);
        window.removeEventListener('resize', updatePosition);
      };
    }
  }, [isVisible, position]);

  // Create tooltip element for portal
  const tooltipElement = isVisible ? (
    ReactDOM.createPortal(
      <div
        id="portal-tooltip"
        className="fixed z-50 px-3 py-2 text-sm font-medium text-white bg-gray-900 dark:bg-gray-700 rounded-lg shadow-sm tooltip max-w-xs"
        style={{
          top: `${tooltipPosition.top}px`,
          left: `${tooltipPosition.left}px`,
          animation: 'tooltipFadeIn 0.2s ease-in-out forwards'
        }}
      >
        {content}
        {/* Arrow indicator based on position */}
        <div 
          className={`absolute ${
            position === 'top' ? 'top-full left-1/2 -translate-x-1/2 border-t-gray-900 dark:border-t-gray-700 border-l-transparent border-r-transparent border-b-transparent' :
            position === 'bottom' ? 'bottom-full left-1/2 -translate-x-1/2 border-b-gray-900 dark:border-b-gray-700 border-l-transparent border-r-transparent border-t-transparent' :
            position === 'left' ? 'left-full top-1/2 -translate-y-1/2 border-l-gray-900 dark:border-l-gray-700 border-t-transparent border-b-transparent border-r-transparent' :
            'right-full top-1/2 -translate-y-1/2 border-r-gray-900 dark:border-r-gray-700 border-t-transparent border-b-transparent border-l-transparent'
          } border-solid border-[6px]`}
        />
      </div>,
      document.body
    )
  ) : null;

  // Clone the children to add event handlers while preserving their props
  const childWithHandlers = React.cloneElement(
    React.Children.only(children),
    {
      ref: childRef,
      onMouseEnter: (e) => {
        setIsVisible(true);
        if (children.props.onMouseEnter) children.props.onMouseEnter(e);
      },
      onMouseLeave: (e) => {
        setIsVisible(false);
        if (children.props.onMouseLeave) children.props.onMouseLeave(e);
      },
      onFocus: (e) => {
        setIsVisible(true);
        if (children.props.onFocus) children.props.onFocus(e);
      },
      onBlur: (e) => {
        setIsVisible(false);
        if (children.props.onBlur) children.props.onBlur(e);
      }
    }
  );

  return (
    <>
      {childWithHandlers}
      {tooltipElement}
    </>
  );
};

export default Tooltip;